<template>
  <div class="comment-list-wrapper">
    <div v-if="commentListData.hasPrevious" class="comment-list-nav">
      <cv-button class="previous" kind="tertiary" size="small" @click="getPreviousComments">
        {{ commentListData.loadPreviousLabel }}
      </cv-button>
    </div>
    <cv-skeleton-text v-if="commentListData.isLoading" :paragraph="true" width="100%" />
    <template v-else>
      <div v-if="useEmptyLabel" class="empty-comment-label">
        {{ commentListData.noCommentLabel }}
      </div>
      <empty-placeholder
        v-if="!hasComment && !isTeacherFeedbackCommentResourceType && !isAnnotationResourceType"
        class="comment-list-placeholder"
      >
        <template #icon>
          <ThreadEmpty />
        </template>
        {{ t("no_comment_label") }}
      </empty-placeholder>
      <div v-else class="comment-list">
        <comment-list-item
          v-for="(comment, index) in comments"
          :key="comment.id"
          v-bind="$attrs"
          :config="config"
          :comment="comment"
          :is-last-comment="isLastComment(index)"
          :comment-list-item-data="commentListItemData"
          :academic-year="academicYear"
          :from-discussion="fromDiscussion"
          :get-mentionable-student-list="getMentionableStudentList"
          :get-mentionable-teacher-list="getMentionableTeacherList"
          :viewed-user-id="viewedUserId"
          :anchor="anchor"
          :allow-ask-salis="allowAskSalis"
          :can-ask-salis="canAskSalis"
          :is-ask-salis-supported-component="isAskSalisSupportedComponent"
          @mark-google-file="handleMarkGoogleFile"
          @mark-copy="handleMarkCopy"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, PropType } from "vue";
import { useStore } from "vuex";
import CommentListItem from "./CommentListItem.vue";
import { COMMENT_RESOURCE_TYPE } from "../comment.constant";
import { NAMESPACE } from "@modules/attempt/service/attempt.constant";
import ThreadEmpty from "@images/placeholder/empty-states/ThreadEmpty.svg";
import { useI18n } from "vue-i18n";
import { CommentResponse } from "@plugins/comment/types.ts";
import EmptyPlaceholder from "@plugins/common/components/EmptyPlaceholder.vue";

const props = defineProps({
  commentListData: {
    required: true,
    type: Object,
  },
  comments: {
    type: Array as PropType<CommentResponse[]>,
    default: () => [],
  },
  academicYear: {
    type: Number,
    default: 0,
  },
  fromDiscussion: {
    type: Boolean,
    default: false,
  },
  config: {
    type: Object,
    default: () => ({}),
  },
  getMentionableTeacherList: {
    type: Function,
    default: () => ({}),
  },
  getMentionableStudentList: {
    type: Function,
    default: () => ({}),
  },
  viewedUserId: {
    type: Number,
    default: 0,
  },
  isAnchorToLastComment: {
    type: Boolean,
    default: false,
  },
  anchor: {
    type: String,
    default: "",
  },
  allowAskSalis: {
    type: Boolean,
    default: false,
  },
  canAskSalis: {
    type: Boolean,
    default: false,
  },
  isAskSalisSupportedComponent: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "previous-comments",
  "markGoogleFile",
  "anchorToLastComment",
  "markCopy",
]);

const store = useStore();
const { t } = useI18n();
const isStudent = computed(() => store.getters[`${NAMESPACE.AUTH}/isStudent`]);

const hasComment = computed(() => props.comments && props.comments.length > 0);

const commentListItemData = computed(() => ({
  editable: props.commentListData.editable,
  deletable: props.commentListData.deletable,
  allowNotify: props.commentListData.allowNotify,
  allowComment: props.commentListData.allowComment,
  tinyMceType: props.commentListData.tinyMceType,
  textSize: props.commentListData.textSize,
  freezeInteraction: props.commentListData.freezeInteraction,
  ownerRestricted: props.commentListData.ownerRestricted,
  resourceUuid: props.commentListData.resourceUuid,
  resourceType: props.commentListData.resourceType,
  isTeacherStudentView: props.commentListData.isTeacherStudentView,
  teamId: props.commentListData.teamId,
  isFeedback: props.commentListData.isFeedback,
  notifyLabel: props.commentListData.notifyLabel,
}));

const isAnnotationResourceType = computed(
  () => props.commentListData.resourceType === COMMENT_RESOURCE_TYPE.ANNOTATION,
);

const isTeacherFeedbackCommentResourceType = computed(() => {
  const type = props.commentListData.resourceType;
  return (
    type === COMMENT_RESOURCE_TYPE.QUESTION_ANSWER ||
    type === COMMENT_RESOURCE_TYPE.ATTEMPT_HISTORY ||
    type === COMMENT_RESOURCE_TYPE.TEAM_ATTEMPT_HISTORY ||
    type === COMMENT_RESOURCE_TYPE.QUESTION ||
    type === COMMENT_RESOURCE_TYPE.ACTIVITY_QUIZ
  );
});

const useEmptyLabel = computed(() => {
  const allowComment =
    isStudent.value && isTeacherFeedbackCommentResourceType.value && props.comments
      ? props.comments?.length > 0
      : props.commentListData.allowComment;
  return !hasComment.value && !allowComment && !props.commentListData.emptyCommentPlaceholder;
});

const isLastComment = (index: number) => {
  return index >= (props.comments?.length ?? 0) - 1;
};

onMounted(() => {
  if (props.isAnchorToLastComment) {
    emit("anchorToLastComment");
  }
});

function getPreviousComments() {
  emit("previous-comments");
}

function handleMarkGoogleFile(data: object) {
  emit("markGoogleFile", data);
}

function handleMarkCopy(data: object) {
  emit("markCopy", data);
}
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";

.comment-list-wrapper {
  position: relative;
  margin: $spacing-05 0;

  .comment-list {
    .comment-list-item {
      &:first-child {
        padding-top: 0;
      }

      &:not(:last-of-type) {
        border-bottom: 1px solid $ui-03;
      }
    }
  }

  .comment-list-nav {
    text-align: center;
    margin: $spacing-03 0 $spacing-05 0;
  }

  .comment-list-placeholder {
    text-align: center;
    padding: $spacing-05 0;
  }
}
</style>
